import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useCallback } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import { useAppSelector } from '../../app/hooks';
import {
  selectFinalOrderStatus,
  selectOrderError,
} from '../../selectors/order';
import {
  selectIsCarPresent,
  selectIsStaffIntervention,
} from '../../selectors/message';
import {
  selectFinalOrderPendingTimeout,
  selectFinalOrderCompletedTimeout,
} from '../../redux/features/config/config.selector';
import { OrderStatus } from '../../reducers/orderSlice.constants';
import { setFinalOrderSubmitted } from '../../redux/features/sessionBoundary/sessionBoundary.slice';
import {
  messagingActions,
  StaffInterceptionType,
} from '../../reducers/messagingSlice';
import useSendMessageHooks from '../../hooks/useSendMessage/useSendMessage.hooks';
import logger from '../../utils/logger';
import Colors from '../../utils/color';
import { selectIntentCards } from '../../selectors/menu';
import { DEFAULT_INTENT_CARDS } from '../../constants/intents';

const ORDER_STATUS_MESSAGES = {
  COMPLETED: 'Final order was successfully submitted to the restaurant.',
  PENDING: 'Final order submission is currently in progress.',
  FAILED: (error: string) =>
    `Final order submission to the restaurant failed.\nReason: ${error}`,
  DEFAULT: '',
} as const;

const ORDER_STATUS_COLORS = {
  COMPLETED: Colors.welcome,
  PENDING: Colors.readBack,
  FAILED: Colors.torchRed,
  DEFAULT: '',
} as const;

// Custom hook for order status management
export const useOrderStatusManagement = () => {
  const dispatch = useDispatch();
  const finalOrderStatus = useAppSelector(selectFinalOrderStatus);
  const orderError = useAppSelector(selectOrderError);
  const isCarPresent = useAppSelector(selectIsCarPresent);
  const pendingTimeout = useAppSelector(selectFinalOrderPendingTimeout);
  const completedTimeout = useAppSelector(selectFinalOrderCompletedTimeout);
  const isStaffIntervention = useSelector(selectIsStaffIntervention);
  const { sendMessage } = useSendMessageHooks();
  const [open, setOpen] = React.useState(false);
  const intentCardsFromBM = useAppSelector(selectIntentCards);
  const INTENT_CARDS = intentCardsFromBM ?? DEFAULT_INTENT_CARDS;

  const handleStaffInterception = useCallback(() => {
    logger.debug('Auto request restaurant staff when final order fails');
    dispatch(
      messagingActions.sendStaffInterception({
        data: {
          type: StaffInterceptionType.AGENT_REQUESTED_STAFF,
        },
      })
    );

    const staffIntent = INTENT_CARDS.find(
      (card) => card.title === 'Request Staff'
    );
    if (staffIntent) {
      sendMessage(staffIntent.messages[0], { intent: staffIntent.intent });
    }
  }, [dispatch, sendMessage]);

  const resetState = useCallback(() => {
    dispatch(setFinalOrderSubmitted(true));
    setOpen(false);
  }, [dispatch]);

  const handlePendingStatus = useCallback(() => {
    setOpen(true);
    const timer = setTimeout(() => {
      logger.debug('Close FinalOrderBackdrop on timeout');
      resetState();
    }, pendingTimeout);

    return () => clearTimeout(timer);
  }, [pendingTimeout]);

  const handleCompletedStatus = useCallback(() => {
    const timer = setTimeout(() => {
      logger.debug(`Close FinalOrderBackdrop when final order completed`);
      resetState();
    }, completedTimeout);

    return () => clearTimeout(timer);
  }, [finalOrderStatus, completedTimeout]);

  const handleFailedStatus = useCallback(() => {
    if (isCarPresent) {
      handleStaffInterception();
    }

    const timer = setTimeout(() => {
      logger.debug(`Close FinalOrderBackdrop when final order failed`);
      resetState();
    }, completedTimeout);

    return () => clearTimeout(timer);
  }, [
    finalOrderStatus,
    isCarPresent,
    handleStaffInterception,
    completedTimeout,
  ]);

  useEffect(() => {
    if (!finalOrderStatus) return;

    if (finalOrderStatus === OrderStatus.pending) {
      return handlePendingStatus();
    } else {
      if (finalOrderStatus === OrderStatus.failed) {
        return handleFailedStatus();
      } else {
        return handleCompletedStatus();
      }
    }
  }, [finalOrderStatus, isCarPresent]);

  useEffect(() => {
    if (isStaffIntervention && open) {
      setOpen(false);
    }
  }, [isStaffIntervention]);

  return {
    open,
    finalOrderStatus,
    orderError,
  };
};

// Utility functions
const getOrderStatusMessage = (
  status: string | undefined,
  orderError: string
): string => {
  switch (status) {
    case 'COMPLETED':
      return ORDER_STATUS_MESSAGES.COMPLETED;
    case 'PENDING':
      return ORDER_STATUS_MESSAGES.PENDING;
    case 'FAILED':
      return ORDER_STATUS_MESSAGES.FAILED(orderError);
    default:
      return ORDER_STATUS_MESSAGES.DEFAULT;
  }
};

const getOrderStatusColor = (status: string | undefined): string => {
  switch (status) {
    case 'COMPLETED':
      return ORDER_STATUS_COLORS.COMPLETED;
    case 'PENDING':
      return ORDER_STATUS_COLORS.PENDING;
    case 'FAILED':
      return ORDER_STATUS_COLORS.FAILED;
    default:
      return ORDER_STATUS_COLORS.DEFAULT;
  }
};

// Main component
export default function FinalOrderBackdrop() {
  const { open, finalOrderStatus, orderError } = useOrderStatusManagement();

  return (
    <Dialog maxWidth="sm" fullWidth open={open}>
      <DialogContent
        sx={{
          color: getOrderStatusColor(finalOrderStatus),
          whiteSpace: 'pre-wrap',
        }}
      >
        {getOrderStatusMessage(finalOrderStatus, orderError)}
      </DialogContent>
    </Dialog>
  );
}
